/* eslint-disable react/jsx-no-literals, no-console */
import languages from "@cospired/i18n-iso-languages";
import enLocale from "@cospired/i18n-iso-languages/langs/en.json";
import {
  faAngleLeft,
  faAngleRight,
  faAnglesLeft,
  faAnglesRight,
  faMagnifyingGlass,
  faSort,
  faSortAlphaAsc,
  faSortAlphaDesc,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import type { FilterFn, Table as ReactTable } from "@tanstack/react-table";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import React from "react";
import { Link } from "react-router-dom";
import { SelectList } from "web-ui";
import { TextInput } from "web-ui/juicy";
import { LegacyButton as Button } from "web-ui/legacy";

import type { VoiceListVoice } from "../api/generalTts";
import { getCombinedVoiceList } from "../api/generalTts";
import styles from "../styles/VoiceFinder.module.scss";
import { getDisplayLanguageName } from "../util/languageUtils";
import { UrlParameterNames } from "../util/urlParameters";

import AiTtsSearch from "./AiTtsSearch";

// Import i18n-iso-languages after standard imports
// Import English locale data
// Register English language data
languages.registerLocale(enLocale);

// Custom language mappings for special exceptions
// Now defined in languageUtils.ts
// Keeping the import of languages and registerLocale for other functionality

// Extended interface to add the displayLanguage property for sorting
// No longer needed but kept for backward compatibility
export interface EnhancedVoiceListVoice extends VoiceListVoice {
  displayLanguage?: string;
}

const languageColumnId = "languageId";
const voiceNameColumnId = "name";
const providerColumnId = "provider";
const genderColumnId = "gender";
const characterColumnId = "character";
const columnHelper = createColumnHelper<EnhancedVoiceListVoice>();

const genericFilterFn: FilterFn<EnhancedVoiceListVoice> = (
  row,
  id,
  filterValue,
) => {
  try {
    // If no filter is set, show all rows
    if (!filterValue) {
      return true;
    }

    const value = row.getValue(id);

    // Handle array of values (multi-select)
    if (Array.isArray(filterValue)) {
      // For all string comparisons, use case-insensitive comparison
      if (typeof value === "string") {
        const rowValue = value.toLowerCase();
        return filterValue.some(
          fv => typeof fv === "string" && fv.toLowerCase() === rowValue,
        );
      }
      return filterValue.includes(value);
    }

    // Handle single value (single-select)
    if (typeof filterValue === "string") {
      // For all string values, use case-insensitive comparison for exact matches
      if (typeof value === "string") {
        // Special handling for languageId to allow filtering by display name, Duolingo ID, or language code
        if (id === languageColumnId) {
          try {
            const langCode = value;
            const searchTerm = filterValue.toLowerCase();

            // Get the display language name
            const displayLanguage = getDisplayLanguageName(langCode);

            // Get the internal Duolingo language ID
            const internalId = row.original.duolingoLanguageId ?? "";

            // Check if any of the three fields contain the search term
            const matchesDisplayName = displayLanguage
              .toLowerCase()
              .includes(searchTerm);
            const matchesLanguageCode = langCode
              .toLowerCase()
              .includes(searchTerm);
            const matchesInternalId = internalId
              .toLowerCase()
              .includes(searchTerm);

            return (
              matchesDisplayName || matchesLanguageCode || matchesInternalId
            );
          } catch (err) {
            console.warn("Error in language filter:", err);
            return false;
          }
        }

        // For provider column, do case-insensitive comparison
        if (id === providerColumnId) {
          const rowValue = value.toLowerCase();
          const filterValueLower = filterValue.toLowerCase();
          return rowValue === filterValueLower;
        }

        // For gender column, do exact case-insensitive comparison
        if (id === genderColumnId) {
          const rowValue = value.toLowerCase();
          const filterValueLower = filterValue.toLowerCase();
          return rowValue === filterValueLower;
        }

        // For other columns, check if the value contains the filter (substring search)
        const rowValue = value.toLowerCase();
        const filterValueLower = filterValue.toLowerCase();
        return rowValue.includes(filterValueLower);
      }

      // If this is an exact match for non-string values
      if (value === filterValue) {
        return true;
      }
    }

    // Default comparison for other types
    return String(value) === String(filterValue);
  } catch (error) {
    console.warn("Error in filter function:", error);
    return false;
  }
};

const baseColumns = [
  columnHelper.accessor(languageColumnId, {
    cell: info => {
      try {
        // Get the original language code
        const languageCode = info.getValue();

        // Get the display name using our helper function
        const displayName = getDisplayLanguageName(languageCode);

        // Get the internal language ID for tooltip
        const internalId = info.row.original.duolingoLanguageId;

        // Create tooltip content
        let tooltipContent = languageCode;
        if (internalId && internalId !== languageCode) {
          tooltipContent += ` (Internal Language ID: ${internalId})`;
        }

        return <span title={tooltipContent}>{displayName}</span>;
      } catch (error) {
        console.warn("Error rendering language cell:", error);
        return <span>Error</span>;
      }
    },
    filterFn: genericFilterFn,
    header: "Language",
    // Custom sorting function that directly compares display language names
    sortingFn: (rowA, rowB) => {
      try {
        const langCodeA: string = rowA.getValue(languageColumnId);
        const langCodeB: string = rowB.getValue(languageColumnId);

        const displayNameA = getDisplayLanguageName(langCodeA) || "";
        const displayNameB = getDisplayLanguageName(langCodeB) || "";

        return displayNameA
          .toLowerCase()
          .localeCompare(displayNameB.toLowerCase());
      } catch (error) {
        console.warn("Error sorting languages:", error);
        return 0;
      }
    },
  }),
  columnHelper.accessor(voiceNameColumnId, {
    cell: info => {
      const voiceName = info.getValue();
      const alternateName = info.row.original.alias;

      // If alternate name exists and isn't "N/A", display it as main content
      // with the official name as tooltip only when they differ
      if (alternateName && alternateName !== "N/A") {
        // Only add tooltip if the names are different
        if (alternateName !== voiceName) {
          return (
            <span title={`Official Name: ${voiceName}`}>{alternateName}</span>
          );
        }
        return alternateName;
      }

      // Otherwise just show the official name
      return voiceName;
    },
    filterFn: (row, id, filterValue) => {
      try {
        // If no filter is set, show all rows
        if (!filterValue) {
          return true;
        }

        const voiceName: string = row.getValue(id);
        const alternateName = row.original.alias || "";

        // For string filter value (text search)
        if (typeof filterValue === "string") {
          const searchTerm = filterValue.toLowerCase();

          // Check if either voice name or alternate name contains the search term
          const matchesVoiceName = voiceName.toLowerCase().includes(searchTerm);
          const matchesAlternateName = alternateName
            .toLowerCase()
            .includes(searchTerm);

          return matchesVoiceName || matchesAlternateName;
        }

        // For array filter values (dropdown selection)
        if (Array.isArray(filterValue)) {
          const voiceNameLower = voiceName.toLowerCase();
          return filterValue.some(
            fv => typeof fv === "string" && fv.toLowerCase() === voiceNameLower,
          );
        }

        return false;
      } catch (error) {
        console.warn("Error in voice name filter function:", error);
        return false;
      }
    },
    header: "Voice Name",
    // Custom sorting function that sorts by the displayed name (alias or official name)
    sortingFn: (rowA, rowB) => {
      try {
        const voiceNameA: string = rowA.getValue(voiceNameColumnId);
        const voiceNameB: string = rowB.getValue(voiceNameColumnId);

        const alternateNameA = rowA.original.alias || "";
        const alternateNameB = rowB.original.alias || "";

        // Use the alternate name if it exists and isn't "N/A", otherwise use the official name
        const displayNameA =
          alternateNameA && alternateNameA !== "N/A"
            ? alternateNameA
            : voiceNameA;

        const displayNameB =
          alternateNameB && alternateNameB !== "N/A"
            ? alternateNameB
            : voiceNameB;

        // Case-insensitive comparison
        return displayNameA
          .toLowerCase()
          .localeCompare(displayNameB.toLowerCase());
      } catch (error) {
        console.warn("Error sorting voice names:", error);
        return 0;
      }
    },
  }),
  columnHelper.accessor(providerColumnId, {
    cell: info => info.renderValue(),
    filterFn: genericFilterFn,
    header: "Provider",
  }),
  columnHelper.accessor(genderColumnId, {
    cell: info => info.renderValue(),
    filterFn: genericFilterFn,
    header: "Gender",
  }),
  columnHelper.accessor(row => row.character ?? "N/A", {
    cell: info => info.renderValue(),
    filterFn: genericFilterFn,
    header: "Character",
    id: characterColumnId,
  }),
];

const Table = ({
  reactTable,
  columnOptions,
}: {
  reactTable: ReactTable<EnhancedVoiceListVoice>;
  columnOptions: Record<string, string[]>;
}) => (
  <table className={styles.table}>
    <thead>
      {reactTable.getHeaderGroups().map(headerGroup => (
        <React.Fragment key={headerGroup.id}>
          <tr key={`${headerGroup.id}-heading`}>
            {headerGroup.headers.map(header => (
              <th colSpan={header.colSpan} key={`${header.id}-heading`}>
                {header.isPlaceholder ? null : (
                  <div className={styles["sortable-header"]}>
                    <div onClick={header.column.getToggleSortingHandler()}>
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}{" "}
                      {header.column.getCanSort() && (
                        <FontAwesomeIcon
                          icon={
                            header.column.getIsSorted() === "asc"
                              ? faSortAlphaAsc
                              : header.column.getIsSorted() === "desc"
                                ? faSortAlphaDesc
                                : faSort
                          }
                        />
                      )}
                    </div>
                  </div>
                )}
              </th>
            ))}
            <th>Search Voice</th>
          </tr>
          <tr key={`${headerGroup.id}-search`}>
            {headerGroup.headers.map(header => (
              <th colSpan={header.colSpan} key={`${header.id}-search`}>
                {header.isPlaceholder ||
                !header.column.getCanFilter() ? null : header.column.id?.startsWith(
                    "dynamic_",
                  ) ? (
                  <SelectList
                    items={(columnOptions[header.column.id] || []).map(
                      (value: string) => ({
                        text: value,
                      }),
                    )}
                    multiple={true}
                    onChange={e => {
                      if (!header.column.id) {
                        return;
                      }

                      const options = columnOptions[header.column.id] || [];
                      const selectedValues = options.filter(
                        (_: string, index: number) =>
                          e.selectedIndices.includes(index),
                      );

                      // An empty array means accept no values,
                      // when we want to accept all values if none are selected,
                      // so we use undefined instead
                      const newFilterValue =
                        e.selectedIndices.length > 0
                          ? selectedValues
                          : undefined;
                      header.column.setFilterValue(newFilterValue);
                    }}
                    popoverPosition={{
                      direction: "down",
                      manualPositioning: true,
                      style: {
                        marginTop: 50,
                        position: "absolute",
                      },
                      zIndex: 100,
                    }}
                    selectedIndices={
                      Array.isArray(header.column.getFilterValue()) &&
                      header.column.id
                        ? (header.column.getFilterValue() as string[])
                            .map(value =>
                              (
                                columnOptions[header.column.id || ""] || []
                              ).indexOf(value),
                            )
                            .filter(index => index !== -1)
                        : []
                    }
                    text={
                      Array.isArray(header.column.getFilterValue()) &&
                      (header.column.getFilterValue() as string[]).length > 0
                        ? `${header.column.columnDef.header?.toString() ?? ""} (${(header.column.getFilterValue() as string[]).length})`
                        : (header.column.columnDef.header?.toString() ?? "")
                    }
                  />
                ) : LOW_CARDINALITY_COLUMNS[header.column.id] ? (
                  <SelectList
                    items={
                      LOW_CARDINALITY_COLUMNS[header.column.id]?.choices?.map(
                        value => ({
                          text: value,
                        }),
                      ) ?? []
                    }
                    multiple={
                      LOW_CARDINALITY_COLUMNS[header.column.id]
                        ?.supportsMultipleChoices ?? false
                    }
                    onChange={e => {
                      const column = LOW_CARDINALITY_COLUMNS[header.column.id];
                      const options = column?.choices ?? [];
                      const supportsMultipleChoices =
                        column?.supportsMultipleChoices ?? false;

                      // Handle differently based on whether multiple selection is supported
                      if (supportsMultipleChoices) {
                        // Multi-select case
                        const choices = options.filter((_, index) =>
                          e.selectedIndices.includes(index),
                        );
                        const newFilterValue =
                          e.selectedIndices.length > 0 ? choices : undefined;
                        header.column.setFilterValue(newFilterValue);
                      } else {
                        // Single-select case
                        if (e.selectedIndices.length === 0) {
                          // Clear filter if nothing selected
                          header.column.setFilterValue(undefined);
                        } else {
                          // Single value filter for single-select dropdown
                          const selectedIndex = e.selectedIndices[0];
                          const selectedValue = options[selectedIndex];

                          // Use the exact value from the dropdown for consistency
                          header.column.setFilterValue(selectedValue);
                        }
                      }
                    }}
                    popoverPosition={{
                      direction: "down",
                      manualPositioning: true,
                      style: {
                        marginTop: 50,
                        maxHeight: 250,
                        position: "absolute",
                      },
                      zIndex: 100,
                    }}
                    selectedIndices={
                      typeof header.column.getFilterValue() === "string"
                        ? [
                            LOW_CARDINALITY_COLUMNS[
                              header.column.id
                            ]?.choices.indexOf(
                              header.column.getFilterValue() as string,
                            ) ?? -1,
                          ]
                        : (
                            (header.column.getFilterValue() ?? []) as string[]
                          ).map(
                            value =>
                              LOW_CARDINALITY_COLUMNS[
                                header.column.id
                              ]?.choices.indexOf(value) ?? -1,
                          )
                    }
                    text={
                      (((Array.isArray(header.column.getFilterValue()) &&
                      (header.column.getFilterValue() as string[]).length > 1
                        ? `${
                            header.column.columnDef.header?.toString() ?? ""
                          } (${
                            (header.column.getFilterValue() as string[]).length
                          }) `
                        : header.column.getFilterValue()) ||
                        header.column.columnDef.header?.toString()) ??
                        "") as string
                    }
                  />
                ) : (
                  <TextInput
                    onChange={e => {
                      const value = e.target.value;
                      // For language column, we need to manually handle the search to also search by display name
                      header.column.setFilterValue(value);
                    }}
                    placeholder={
                      header.column.id === languageColumnId
                        ? "Search by name or code"
                        : "Filter"
                    }
                    type="search"
                    value={(header.column.getFilterValue() ?? "") as string}
                  />
                )}
              </th>
            ))}
            <th />
          </tr>
        </React.Fragment>
      ))}
    </thead>
    <tbody>
      {reactTable.getRowModel().rows.map(row => (
        <tr key={row.id}>
          {row.getVisibleCells().map(cell => (
            <td key={cell.id}>
              {flexRender(cell.column.columnDef.cell, cell.getContext())}
            </td>
          ))}
          <td>
            {row.original.provider === "RECORDED" ? (
              "N/A"
            ) : (
              <Link
                to={`/generate?${UrlParameterNames.voice}=${row.original.name}`}
              >
                <FontAwesomeIcon icon={faMagnifyingGlass} />
              </Link>
            )}
          </td>
        </tr>
      ))}
    </tbody>
  </table>
);

const TopControls = ({
  reactTable,
}: {
  reactTable: ReactTable<EnhancedVoiceListVoice>;
}) => {
  const itemsPerPageOptions = [10, 20, 30, 40, 50];

  return (
    <div className={styles["top-controls"]}>
      <div>
        Jump to page:{" "}
        <TextInput
          inputMode="numeric"
          onChange={e => {
            const page = e.target.value ? Number(e.target.value) - 1 : 0;
            reactTable.setPageIndex(page);
          }}
          placeholder="Page Number"
          type="number"
        />
      </div>
      <div>
        Results per page:{" "}
        <SelectList
          items={itemsPerPageOptions.map(num => ({
            text: num.toString(),
          }))}
          onChange={e => {
            reactTable.setPageSize(
              Number(
                itemsPerPageOptions[e.selectedIndices[0]] ??
                  itemsPerPageOptions[0],
              ),
            );
          }}
          selectedIndices={[
            itemsPerPageOptions.indexOf(
              reactTable.getState().pagination.pageSize,
            ),
          ]}
        />
      </div>
    </div>
  );
};

const BottomControls = ({
  reactTable,
}: {
  reactTable: ReactTable<EnhancedVoiceListVoice>;
}) => (
  <div>
    <div>
      Page <strong>{reactTable.getState().pagination.pageIndex + 1}</strong> of{" "}
      <strong>{reactTable.getPageCount()}</strong>
    </div>
    <div className={styles["page-buttons"]}>
      <Button
        color="macaw"
        disabled={!reactTable.getCanPreviousPage()}
        onClick={() => reactTable.setPageIndex(0)}
        variant="stroke"
      >
        <FontAwesomeIcon icon={faAnglesLeft} />
      </Button>
      <Button
        color="macaw"
        disabled={!reactTable.getCanPreviousPage()}
        onClick={() => reactTable.previousPage()}
        variant="stroke"
      >
        <FontAwesomeIcon icon={faAngleLeft} />
      </Button>
      <Button
        color="macaw"
        disabled={!reactTable.getCanNextPage()}
        onClick={() => reactTable.nextPage()}
        variant="stroke"
      >
        <FontAwesomeIcon icon={faAngleRight} />
      </Button>
      <Button
        color="macaw"
        disabled={!reactTable.getCanNextPage()}
        onClick={() => reactTable.setPageIndex(reactTable.getPageCount() - 1)}
        variant="stroke"
      >
        <FontAwesomeIcon icon={faAnglesRight} />
      </Button>
    </div>
  </div>
);

// Add constants for limiting options to prevent performance issues
const MAX_OPTIONS_PER_COLUMN = 500; // Maximum number of unique values to show for a column

// Convert sets to sorted arrays with a reasonable limit
const sortOptions = (valueArray: string[], columnId?: string): string[] => {
  try {
    // Make a copy before sorting to avoid mutating the original array
    const arrayCopy = [...valueArray];

    // Special sorting for language column - use natural sorting for language names
    if (columnId === languageColumnId) {
      return arrayCopy.sort((a, b) =>
        // Case-insensitive lexicographical sort
        a.toLowerCase().localeCompare(b.toLowerCase()),
      );
    }

    // Default sorting for other columns
    return arrayCopy.sort((a, b) => a.localeCompare(b));
  } catch (error) {
    console.warn("Error sorting options:", error);
    return [...valueArray]; // Return unsorted copy if sorting fails
  }
};

// Restore original LOW_CARDINALITY_COLUMNS for the character field
const LOW_CARDINALITY_COLUMNS: Record<
  string,
  { choices: string[]; supportsMultipleChoices: boolean } | undefined
> = {
  [characterColumnId]: {
    choices: [
      "N/A",
      "BEA",
      "EDDY",
      "JUNIOR",
      "LILY",
      "LIN",
      "LUCY",
      "OSCAR",
      "VIKRAM",
      "ZARI",
      "FALSTAFF",
    ],
    supportsMultipleChoices: true,
  },
  [genderColumnId]: {
    choices: ["MALE", "FEMALE"],
    supportsMultipleChoices: false,
  },
  [providerColumnId]: {
    choices: ["AMAZON", "AZURE", "ELEVENLABS", "GOOGLE", "RECORDED"],
    supportsMultipleChoices: false,
  },
};

const VoiceFinder = () => {
  const [data, setData] = React.useState<EnhancedVoiceListVoice[]>([]);
  const [columns, setColumns] = React.useState(baseColumns);
  const [columnOptions, setColumnOptions] = React.useState<
    Record<string, string[]>
  >({});

  // Create a memoized version of the data to improve performance
  const memoizedData = React.useMemo(() => data, [data]);

  // Create a memoized version of the columns to improve performance
  const memoizedColumns = React.useMemo(() => columns, [columns]);

  React.useEffect(() => {
    const fetchData = async () => {
      try {
        const voiceList = (await getCombinedVoiceList()) || [];
        setColumns(baseColumns);
        setData(voiceList);

        // Build column options from base data only
        const options: Record<string, Set<string>> = {};

        // Initialize options sets for all columns
        baseColumns.forEach(column => {
          if (column.id) {
            options[column.id] = new Set<string>();
          }
        });

        // Collect unique values
        voiceList.forEach(item => {
          baseColumns.forEach(column => {
            if (!column.id) {
              return;
            }

            let value: string;

            if (column.id === "duolingoLanguageId") {
              // Skip this column as it's now combined with languageId
              return;
            } else if (column.id === languageColumnId) {
              // Use display language name for language column
              const langCode = item[languageColumnId] || "N/A";
              value = getDisplayLanguageName(langCode);
            } else if (column.id === characterColumnId) {
              value = item.character ?? "N/A";
            } else {
              // Standard field
              value =
                (item as unknown as Record<string, string>)[column.id] || "N/A";
            }

            options[column.id].add(value);
          });
        });

        // Convert sets to sorted arrays with a reasonable limit
        const finalOptions: Record<string, string[]> = {};
        Object.entries(options).forEach(([key, valueSet]) => {
          // Convert to array and sort
          const valueArray = Array.from(valueSet);

          // If there are too many options, limit them to prevent performance issues
          if (valueArray.length > MAX_OPTIONS_PER_COLUMN) {
            console.warn(
              `Column ${key} has too many options (${valueArray.length}). Limiting to ${MAX_OPTIONS_PER_COLUMN}.`,
            );
            finalOptions[key] = sortOptions(
              valueArray.slice(0, MAX_OPTIONS_PER_COLUMN),
              key,
            );
          } else {
            finalOptions[key] = sortOptions(valueArray, key);
          }
        });

        setColumnOptions(finalOptions);
      } catch (error) {
        console.error("Error loading voice data:", error);
        const voiceList = (await getCombinedVoiceList()) || [];
        setColumns(baseColumns);
        setData(voiceList);
      }
    };

    fetchData();
  }, []);

  // Optimize the table to prevent render bottlenecks
  const reactTable = useReactTable({
    columns: memoizedColumns,
    data: memoizedData,
    enableMultiSort: false,
    enableSorting: true,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    initialState: {
      pagination: {
        pageSize: 50,
      },
      sorting: [{ desc: false, id: languageColumnId }],
    },
  });

  return (
    <div className={styles["container"]}>
      <h1>Voice Finder</h1>
      <AiTtsSearch
        columnOptions={columnOptions}
        data={memoizedData}
        reactTable={reactTable}
      />
      <TopControls reactTable={reactTable} />
      <Table columnOptions={columnOptions} reactTable={reactTable} />
      <BottomControls reactTable={reactTable} />
    </div>
  );
};

export default VoiceFinder;
